import { useEffect } from "react";

/**
 * Tracks the visible viewport height (excludes on-screen keyboard).
 * Works for both PWA and Capacitor native apps.
 *
 * Sets CSS vars on :root:
 *   --app-height      — visible viewport height (use instead of 100vh)
 *   --keyboard-height — height of the on-screen keyboard (0 when hidden)
 *   --safe-area-bottom — overridden to 0px when keyboard is open
 *
 * Adds/removes class on <body>:
 *   keyboard-open
 *
 * Strategy:
 *  - PWA: detect keyboard via visualViewport.height shrinking vs window.innerHeight
 *  - Native Capacitor: use @capacitor/keyboard plugin events (resize:'body' mode
 *    keeps window.innerHeight == visualViewport.height so diff-based detection fails)
 */

// Detect Capacitor native without importing @capacitor/core
// (avoids potential ESM/version conflicts with the native bridge)
function isCapacitorNative(): boolean {
  const cap = (window as any).Capacitor;
  return typeof cap !== "undefined" && cap.isNativePlatform?.() === true;
}

export default function useViewportHeight() {
  useEffect(() => {
    const vv = window.visualViewport;
    const isNative = isCapacitorNative();

    // Capture initial full height ONCE — on iOS PWA standalone,
    // window.innerHeight may shrink together with visualViewport.height
    // when the keyboard opens, making diff-based detection fail.
    const initialFullHeight = window.innerHeight;

    const updateViewport = () => {
      // Native: CSS vars are managed exclusively by Capacitor keyboard events.
      // Ignore visualViewport changes here to prevent race conditions.
      if (isNative) return;

      const visibleHeight = vv?.height ?? window.innerHeight;
      const offsetTop = Math.round(vv?.offsetTop ?? 0);
      // Use the larger of saved initial height and current innerHeight
      // to reliably detect keyboard even when iOS shrinks the webview.
      const fullHeight = Math.max(initialFullHeight, window.innerHeight);
      const keyboardHeight = Math.max(0, fullHeight - visibleHeight - offsetTop);

      document.documentElement.style.setProperty("--app-height", `${Math.round(visibleHeight)}px`);
      document.documentElement.style.setProperty("--app-offset-top", `${offsetTop}px`);
      document.documentElement.style.setProperty("--keyboard-height", `${keyboardHeight}px`);

      const kbOpen = keyboardHeight > 50;
      if (kbOpen) {
        document.body.classList.add("keyboard-open");
        document.documentElement.style.setProperty("--safe-area-bottom", "0px");
      } else {
        document.body.classList.remove("keyboard-open");
        document.documentElement.style.removeProperty("--safe-area-bottom");
      }
    };

    const cleanupHandlers: Array<() => void> = [];

    if (isNative) {
      const isAndroid = (window as any).Capacitor?.getPlatform?.() === "android";

      // Set initial values once — Capacitor keyboard events take over from here.
      // On Android, do NOT set a pixel --app-height: window.innerHeight at startup
      // can be smaller than the real viewport (StatusBar overlay / nav-bar timing),
      // leaving empty space at the bottom.  The CSS default "100%" is correct for
      // position:fixed elements.
      if (!isAndroid) {
        document.documentElement.style.setProperty("--app-height", `${initialFullHeight}px`);
      }
      document.documentElement.style.setProperty("--app-offset-top", "0px");
      document.documentElement.style.setProperty("--keyboard-height", "0px");

      try {
        const CapKb = (window as any).Capacitor?.Plugins?.Keyboard;
        if (CapKb?.addListener) {
          // Save full height just before the keyboard animates in (window.innerHeight
          // is the WKWebView height and stays constant in resize:'body' mode, but
          // we capture it here as an explicit reference to be safe).
          let savedHeight = initialFullHeight;

          const applyKeyboardOpen = (kbH: number) => {
            savedHeight = window.innerHeight; // refresh in case device rotated
            document.documentElement.style.setProperty("--app-height", `${Math.round(savedHeight - kbH)}px`);
            document.documentElement.style.setProperty("--keyboard-height", `${Math.round(kbH)}px`);
            document.body.classList.add("keyboard-open");
            document.documentElement.style.setProperty("--safe-area-bottom", "0px");
          };

          const applyKeyboardClosed = () => {
            if (isAndroid) {
              // Revert to CSS default 100% — more reliable than a pixel value
              document.documentElement.style.removeProperty("--app-height");
            } else {
              document.documentElement.style.setProperty("--app-height", `${savedHeight}px`);
            }
            document.documentElement.style.setProperty("--keyboard-height", "0px");
            document.body.classList.remove("keyboard-open");
            document.documentElement.style.removeProperty("--safe-area-bottom");
          };

          const safeAddListener = (event: string, cb: (...args: any[]) => void) => {
            const result = CapKb.addListener(event, cb);
            if (result && typeof result.then === "function") {
              result.then((h: any) => cleanupHandlers.push(() => h?.remove?.())).catch(() => {});
            } else if (result) {
              cleanupHandlers.push(() => result.remove?.());
            }
          };

          safeAddListener("keyboardWillShow", (info: { keyboardHeight?: number }) => {
            applyKeyboardOpen(info?.keyboardHeight ?? 0);
          });

          safeAddListener("keyboardWillHide", applyKeyboardClosed);

          // Safety net: keyboardDidHide fires after animation is fully complete,
          // ensuring --app-height is correct even if timing differs per iOS version.
          safeAddListener("keyboardDidHide", applyKeyboardClosed);
        }
      } catch (e) {
        console.warn("[useViewportHeight] Capacitor keyboard events unavailable:", e);
      }
    } else {
      // PWA / browser: use visualViewport for keyboard detection
      updateViewport();
      vv?.addEventListener("resize", updateViewport);
      vv?.addEventListener("scroll", updateViewport);
      window.addEventListener("resize", updateViewport);
    }

    return () => {
      vv?.removeEventListener("resize", updateViewport);
      vv?.removeEventListener("scroll", updateViewport);
      window.removeEventListener("resize", updateViewport);
      cleanupHandlers.forEach((fn) => fn());
    };
  }, []);
}
